// src/components/MonerisCheckout.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const MonerisCheckout = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [showMonerisCheckout, setShowMonerisCheckout] = useState(false);

  useEffect(() => {
    const loadPaymentData = async () => {
      try {
        const { data: payment, error } = await supabase
          .from('payments')
          .select('*')
          .eq('short_id', paymentId)
          .single();
        
        if (error || !payment) {
          setIsError(true);
          setStatusMessage('Invalid or expired payment link');
          return;
        }

        const createdAt = new Date(payment.created_at);
        const now = new Date();
        const hoursDiff = (now - createdAt) / (1000 * 60 * 60);

        if (hoursDiff > 24) {
          setIsExpired(true);
          setIsError(true);
          setStatusMessage('This payment link has expired');
          return;
        }

        if (payment.status === 'completed') {
          setIsError(true);
          setStatusMessage('This payment has already been processed');
          return;
        }

        setPaymentData(payment);
      } catch (error) {
        console.error('Error loading payment data:', error);
        setIsError(true);
        setStatusMessage('Error loading payment data');
      }
    };

    loadPaymentData();
  }, [paymentId]);

  const showMessage = (message, error = false) => {
    setStatusMessage(message);
    setIsError(error);
  };

  const generateNewOrderNo = () => {
    return `${paymentData.transaction_id}-${Date.now().toString(36)}`;
  };

  const handlePaymentComplete = async (paymentResult) => {
    try {
      const { error } = await supabase
        .from('payments')
        .update({
          status: paymentResult.response_code === '027' ? 'completed' : 'failed',
          completed_at: new Date().toISOString(),
          payment_result: paymentResult,
          attempts: paymentData.attempts + 1
        })
        .eq('short_id', paymentId);

      if (error) throw error;
      
      console.log('Payment completed:', paymentResult);
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const initializeCheckout = async () => {
    if (!paymentData) {
      showMessage('Payment data not found', true);
      return;
    }
  
    try {
      setIsProcessing(true);
      setShowMonerisCheckout(true);
      showMessage("Initializing payment process...");
      
      // Generar un nuevo order_no único cada vez
      const newOrderNo = generateNewOrderNo();
      
      const response = await fetch('/server/immigrationpros_payments_function/api/preload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          store_id: 'gwca081601',
          api_token: 'PKXuvi7CZcHMgwEZLEf3',
          checkout_id: paymentData.checkout_id,
          txn_total: parseFloat(paymentData.amount).toFixed(2),
          environment: 'prod',
          action: 'preload',
          order_no: newOrderNo,
          cust_id: 'CUST-' + paymentData.transaction_id,
          dynamic_descriptor: paymentData.service || 'Immigration Pros',
          cart: {
            items: [
              {
                description: paymentData.description || paymentData.service,
                product_code: 'PROD1',
                unit_cost: paymentData.amount,
                quantity: '1',
              },
            ],
            subtotal: paymentData.amount,
            tax: {
              amount: '0.00',
              description: 'Tax',
              rate: '0',
            },
          },
          billing: paymentData.email ? {
            email: paymentData.email
          } : undefined
        }),
      });
      
      const data = await response.json();

      if (data.response && data.response.success === "true") {
        const myCheckout = new window.monerisCheckout();
        myCheckout.setMode("prod");
        myCheckout.setCheckoutDiv("monerisCheckout");
        myCheckout.setCallback("page_loaded", function (data) {
          console.log("Page loaded:", data);
          if (data.response_code === "001") {
            showMessage("Payment page loaded successfully");
          }
        });

        myCheckout.setCallback("cancel_transaction", function (data) {
          console.log("Transaction cancelled:", data);
          showMessage("Transaction cancelled by user", true);
          setIsProcessing(false);
          setShowMonerisCheckout(false);
          myCheckout.closeCheckout();
        });

        myCheckout.setCallback("error_event", function (data) {
          console.log("Error:", data);
          showMessage("Transaction error: " + data.response_code, true);
          setIsProcessing(false);
          setShowMonerisCheckout(false);
          myCheckout.closeCheckout();
        });

        myCheckout.setCallback("payment_receipt", function (data) {
          console.log("Payment receipt:", data);
          showMessage("Receipt generated successfully");
        });

        myCheckout.setCallback("payment_complete", async function (data) {
          console.log("Payment completed:", data);
          let message = "Payment completed successfully!";

          if (data.installments) {
            message += " Payment processed with Visa Installments.";
            console.log("Installment details:", data.installments);
          }

          const paymentResult = {
            ...data,
            order_no: newOrderNo,
            original_transaction_id: paymentData.transaction_id
          };

          await handlePaymentComplete(paymentResult);
          showMessage(message);
          setIsProcessing(false);
          setShowMonerisCheckout(false);

          setTimeout(() => {
            myCheckout.closeCheckout();
          }, 3000);
        });

        myCheckout.startCheckout(data.response.ticket);
      } else {
        console.error('Error in response:', data);
        showMessage('Error initializing checkout: ' + JSON.stringify(data), true);
        setIsProcessing(false);
        setShowMonerisCheckout(false);
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('Error communicating with server: ' + error.message, true);
      setIsProcessing(false);
      setShowMonerisCheckout(false);
    }
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <img
              src="https://immigrationpros.ca/wp-content/uploads/2024/07/Logo-Immigrationpros-New-1.png"
              alt="Immigration Pros"
              className="h-16 mx-auto"
            />
          </div>
          <div className="bg-red-50 p-6 rounded-xl text-center shadow-sm">
            <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
            <p className="text-red-600">{statusMessage}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 px-8 py-3 bg-red-600 text-white rounded-xl 
                       hover:bg-red-700 transition-all duration-300 shadow-sm"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <img
              src="https://immigrationpros.ca/wp-content/uploads/2024/07/Logo-Immigrationpros-New-1.png"
              alt="Immigration Pros"
              className="h-16 mx-auto"
            />
          </div>
          <div className="bg-blue-50 p-6 rounded-xl text-center text-blue-700">
            Loading payment details...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="https://immigrationpros.ca/wp-content/uploads/2024/07/Logo-Immigrationpros-New-1.png"
            alt="Immigration Pros"
            className="h-16 mx-auto"
          />
        </div>

        <div className="space-y-6">
          {/* Details Card */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Service */}
            {paymentData.service && (
              <div className="p-4 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Service</span>
                  <span className="font-medium text-gray-900">{paymentData.service}</span>
                </div>
              </div>
            )}

            {/* Description */}
            {paymentData.description && (
              <div className="p-4 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Description</span>
                  <span className="font-medium text-gray-900">{paymentData.description}</span>
                </div>
              </div>
            )}

            {/* Transaction ID */}
            <div className="p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Transaction ID</span>
                <span className="font-medium text-gray-900">{paymentData.transaction_id}</span>
              </div>
            </div>
          </div>

          {/* Amount and Payment Card */}
          <div className="bg-red-50 rounded-xl p-6 shadow-sm">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div>
                <span className="text-sm text-gray-600">Amount to Pay</span>
                <div className="mt-1">
                  <span className="text-4xl font-bold text-red-600">
                    ${parseFloat(paymentData.amount).toFixed(2)}
                  </span>
                  <span className="ml-2 text-lg text-gray-600">CAD</span>
                </div>
              </div>
              {!isProcessing && (
                <button
                  onClick={initializeCheckout}
                  className="w-full md:w-auto bg-red-600 text-white px-8 py-3 rounded-xl
                           hover:bg-red-700 transition-all duration-300
                           shadow-md hover:shadow-lg transform hover:-translate-y-0.5
                           text-lg font-medium"
                >
                  Pay Now
                </button>
              )}
            </div>
          </div>

          {/* Status Message */}
          {statusMessage && (
            <div className={`rounded-xl p-4 text-center shadow-sm ${
              isError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
            }`}>
              {statusMessage}
            </div>
          )}
        </div>

        {/* Moneris Checkout Container */}
        {showMonerisCheckout && (
          <div 
            id="monerisCheckout" 
            className="w-full mt-8 bg-gray-50 rounded-xl overflow-hidden"
            style={{ minHeight: '600px' }}
          />
        )}

        {/* Footer */}

      </div>
    </div>
  );
};

export default MonerisCheckout;